import React, { useEffect, useMemo, useState } from "react";
import {
  fetchAdminDailyPlayerGame,
  fetchDailyPlayerQueue,
  notifyDailyPlayerGame,
  queueDailyPlayerGame,
  removeDailyPlayerFromQueue,
  searchDailyPlayerGamePlayers,
  setDailyPlayerGameAnswer,
} from "../api";

const panel = {
  padding: 18,
  border: "1px solid rgba(49,212,139,.25)",
  borderRadius: 16,
  background: "rgba(49,212,139,.05)",
};

const button = {
  padding: "11px 15px",
  border: 0,
  borderRadius: 11,
  background: "#31d48b",
  color: "#052016",
  fontWeight: 900,
  cursor: "pointer",
};

const dateValue = (offset = 1) => {
  const value = new Date();
  value.setDate(value.getDate() + offset);
  return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, "0")}-${String(value.getDate()).padStart(2, "0")}`;
};

const prettyDate = (value) => new Intl.DateTimeFormat("en-GB", {
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
}).format(new Date(`${value}T12:00:00`));

export default function AdminDailyPlayerPanel() {
  const [game, setGame] = useState(null);
  const [queue, setQueue] = useState([]);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [scheduledDate, setScheduledDate] = useState(dateValue(1));
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const minDate = useMemo(() => dateValue(0), []);

  const load = async () => {
    const [today, upcoming] = await Promise.all([
      fetchAdminDailyPlayerGame(),
      fetchDailyPlayerQueue(45),
    ]);
    setGame(today);
    setQueue(upcoming.queue || []);
  };

  useEffect(() => {
    load().catch(() => setNotice("Could not load the Daily Player schedule."));
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    const id = setTimeout(() => {
      searchDailyPlayerGamePlayers(q.trim())
        .then((response) => setResults(response.players || []))
        .catch(() => setResults([]));
    }, 180);
    return () => clearTimeout(id);
  }, [q]);

  const selectPlayer = (player) => {
    setSelected(player);
    setQ("");
    setResults([]);
    setNotice("");
  };

  const makeToday = async () => {
    if (!selected) return;
    const attempts = Number(game?.attempts || 0);
    const force = attempts > 0
      ? window.confirm(`${attempts} player(s) have already started today's puzzle. Change the answer to ${selected.name} and reset those attempts?`)
      : false;
    if (attempts > 0 && !force) return;
    setBusy(true);
    setNotice("");
    try {
      const response = await setDailyPlayerGameAnswer(selected.player_id, force);
      const reset = response.reset_attempts ? ` ${response.reset_attempts} attempt(s) were reset.` : "";
      setNotice(`${response.player.name} is now today's live player.${reset}`);
      setSelected(null);
      await load();
    } catch (error) {
      setNotice(error?.response?.data?.detail || "Could not update today's player.");
    } finally {
      setBusy(false);
    }
  };

  const addToQueue = async () => {
    if (!selected || !scheduledDate) return;
    setBusy(true);
    setNotice("");
    try {
      const response = await queueDailyPlayerGame(selected.player_id, scheduledDate);
      setNotice(`${response.player.name} queued for ${prettyDate(response.date)} at 10:00 UK time.`);
      setSelected(null);
      setScheduledDate(dateValue(1));
      await load();
    } catch (error) {
      setNotice(error?.response?.data?.detail || "Could not queue that player.");
    } finally {
      setBusy(false);
    }
  };

  const removeQueued = async (puzzleDate) => {
    if (!window.confirm(`Remove the queued player for ${prettyDate(puzzleDate)}?`)) return;
    setBusy(true);
    setNotice("");
    try {
      await removeDailyPlayerFromQueue(puzzleDate);
      setNotice(`Removed ${prettyDate(puzzleDate)} from the queue.`);
      await load();
    } catch (error) {
      setNotice(error?.response?.data?.detail || "Could not remove that queued player.");
    } finally {
      setBusy(false);
    }
  };

  const notify = async () => {
    setBusy(true);
    setNotice("");
    try {
      const response = await notifyDailyPlayerGame();
      setNotice(`Published — ${response.devices_notified} device${response.devices_notified === 1 ? "" : "s"} notified.`);
      await load();
    } catch (error) {
      setNotice(error?.response?.data?.detail || "Could not send the notification.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "grid", gap: 18 }}>
      <div style={panel}>
        <div style={{ fontSize: 11, fontWeight: 900, letterSpacing: 1.5, color: "#31d48b" }}>LIVE PUZZLE</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: "6px 0", color: "#f0fdf4" }}>{game?.answer?.name || "Answer hidden from players"}</h3>
            <p style={{ margin: 0, color: "#91aa9c" }}>
              {game?.answer?.team_name || "Current player"} · {game?.answer?.nationality || "Football"}
            </p>
            <small style={{ display: "block", color: "#789081", marginTop: 9 }}>{game?.attempts || 0} player(s) have started today.</small>
          </div>
          {game?.answer && (
            <button type="button" disabled={busy} onClick={notify} style={button}>Send notification now</button>
          )}
        </div>
      </div>

      <section style={{ ...panel, background: "linear-gradient(145deg, rgba(8,31,21,.96), rgba(4,17,12,.98))" }}>
        <div style={{ color: "#31d48b", fontSize: 11, fontWeight: 900, letterSpacing: 1.5 }}>QUEUE A PLAYER</div>
        <h3 style={{ margin: "7px 0 5px", color: "#f0fdf4" }}>Schedule the next daily challenge</h3>
        <p style={{ margin: "0 0 15px", color: "#91aa9c", lineHeight: 1.5 }}>
          Choose a player and date. It will go live automatically at 10:00 Europe/London and one push notification will be sent to eligible devices.
        </p>

        <div style={{ position: "relative" }}>
          <input
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder="Search the player catalogue…"
            style={{ width: "100%", boxSizing: "border-box", padding: "15px 16px", borderRadius: 13, border: "1px solid #31d48b", background: "#06110b", color: "white", fontSize: 16 }}
          />
          {results.length > 0 && (
            <div style={{ border: "1px solid #234c37", borderRadius: 13, overflow: "hidden", marginTop: 7 }}>
              {results.map((player) => (
                <button
                  type="button"
                  disabled={busy}
                  key={player.player_id}
                  onClick={() => selectPlayer(player)}
                  style={{ width: "100%", display: "flex", alignItems: "center", gap: 12, padding: 11, border: 0, borderBottom: "1px solid #234c37", background: "#0b1e14", color: "white", textAlign: "left", cursor: "pointer" }}
                >
                  {player.photo ? <img src={player.photo} alt="" style={{ width: 44, height: 44, borderRadius: "50%", objectFit: "cover" }} /> : <span style={{ width: 44 }} />}
                  <span><strong style={{ display: "block" }}>{player.name}</strong><small style={{ color: "#91aa9c" }}>{player.team_name} · {player.nationality}</small></span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div style={{ display: "grid", gridTemplateColumns: "minmax(220px, 1fr) minmax(180px, .55fr)", gap: 12, marginTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: 13, border: "1px solid rgba(49,212,139,.3)", borderRadius: 13, background: "rgba(49,212,139,.07)" }}>
              {selected.photo && <img src={selected.photo} alt="" style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover" }} />}
              <div><strong style={{ display: "block", color: "#f0fdf4" }}>{selected.name}</strong><small style={{ color: "#91aa9c" }}>{selected.team_name} · {selected.league_name}</small></div>
            </div>
            <input
              type="date"
              min={minDate}
              value={scheduledDate}
              onChange={(event) => setScheduledDate(event.target.value)}
              style={{ padding: "13px", borderRadius: 13, border: "1px solid #234c37", background: "#06110b", color: "#f0fdf4", colorScheme: "dark", fontWeight: 800 }}
            />
            <button type="button" disabled={busy} onClick={addToQueue} style={button}>Add to 10:00 queue</button>
            <button type="button" disabled={busy} onClick={makeToday} style={{ ...button, background: "transparent", color: "#86efac", border: "1px solid #31d48b" }}>Make live today instead</button>
          </div>
        )}
      </section>

      <section>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "end", marginBottom: 10 }}>
          <div><div style={{ color: "#31d48b", fontSize: 11, fontWeight: 900, letterSpacing: 1.5 }}>UPCOMING</div><h3 style={{ margin: "5px 0 0", color: "#f0fdf4" }}>Daily Player queue</h3></div>
          <span style={{ color: "#789081", fontSize: 12 }}>10:00 UK · automatic notification</span>
        </div>
        {queue.length === 0 ? (
          <div style={{ ...panel, color: "#91aa9c" }}>Nothing queued yet. Pick tomorrow&apos;s player above.</div>
        ) : (
          <div style={{ display: "grid", gap: 9 }}>
            {queue.map((entry) => (
              <div key={entry.date} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: 13, border: "1px solid rgba(134,239,172,.14)", borderRadius: 14, background: "rgba(255,255,255,.025)" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                  {entry.player?.photo && <img src={entry.player.photo} alt="" style={{ width: 42, height: 42, borderRadius: "50%", objectFit: "cover" }} />}
                  <div style={{ minWidth: 0 }}><strong style={{ color: "#f0fdf4", display: "block" }}>{entry.player?.name}</strong><small style={{ color: "#91aa9c" }}>{prettyDate(entry.date)} · {entry.status === "live" ? "Live" : "Queued for 10:00"}</small></div>
                </div>
                {entry.status !== "live" && <button type="button" disabled={busy} onClick={() => removeQueued(entry.date)} style={{ padding: "8px 11px", borderRadius: 9, border: "1px solid rgba(248,113,113,.35)", background: "rgba(248,113,113,.07)", color: "#fca5a5", fontWeight: 800, cursor: "pointer" }}>Remove</button>}
              </div>
            ))}
          </div>
        )}
      </section>

      {notice && <div style={{ color: notice.toLowerCase().includes("could not") ? "#fca5a5" : "#86efac", fontWeight: 700 }}>{notice}</div>}
    </div>
  );
}
